import React, { Component } from "react";
import PropTypes from "prop-types";
import { curveLinear } from "d3-shape";

import BaselineLineSeries from "./BaselineLineSeries";
import BaselineAreaSeries from "./BaselineAreaSeries";
import BaselineSeparator from "./BaselineSeparator";
import BaselineHandle from "./BaselineHandle";
import GenericChartComponent from "../../GenericChartComponent";

class BaselineSeries extends Component {
  constructor(props) {
    super(props);

    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragComplete = this.handleDragComplete.bind(this);
    this.handleHover = this.handleHover.bind(this);
    this.calculateSeparatorFromData =
      this.calculateSeparatorFromData.bind(this);

    this.state = {
      hovering: false,
      dragSeparatorPrice: null,
      separatorPrice: null, // Will be calculated from data
      isDragging: false, // Track drag state
    };

    // Animation frame for smooth drag updates
    this.dragAnimationFrame = null;
  }

  calculateSeparatorFromData(moreProps) {
    if (this.state.separatorPrice !== null) return; // Only calculate once initially

    const { plotData } = moreProps;
    const { chartConfig } = moreProps;

    if (
      !plotData ||
      plotData.length === 0 ||
      !chartConfig ||
      !chartConfig.yScale
    ) {
      return;
    }

    // Get the visible Y domain (price range) from yScale
    const yScale = chartConfig.yScale;
    const yDomain = yScale.domain();

    if (!yDomain || yDomain.length < 2) return;

    // Set separator to middle of visible price range (only on initial calculation)
    const [minPrice, maxPrice] = yDomain;
    const separatorPrice = (minPrice + maxPrice) / 2;

    this.setState({ separatorPrice });
  }

  handleYAxisZoom(moreProps) {
    // This method will be called when Y-axis zoom events occur
    const { separatorPrice, isDragging } = this.state;

    // Don't recenter if user is dragging the separator manually
    if (isDragging || separatorPrice === null) return;

    const { chartConfig, plotData } = moreProps;
    if (
      !chartConfig ||
      !chartConfig.yScale ||
      !plotData ||
      plotData.length === 0
    )
      return;

    const yScale = chartConfig.yScale;
    const currentDomain = yScale.domain();
    const [currentMin, currentMax] = currentDomain;

    // Check if separator is not centered (allow some tolerance)
    const currentCenter = (currentMin + currentMax) / 2;
    const tolerance = (currentMax - currentMin) * 0.05; // 5% tolerance

    if (Math.abs(separatorPrice - currentCenter) > tolerance) {
      // Calculate data extents from visible plot data
      const dataExtents = plotData.reduce(
        (acc, d) => {
          const high = d.high || d.close || d.value;
          const low = d.low || d.close || d.value;
          return {
            min: Math.min(acc.min, low),
            max: Math.max(acc.max, high),
          };
        },
        { min: Infinity, max: -Infinity },
      );

      // Calculate distances from separator to data extremes
      const distanceToTop = Math.abs(dataExtents.max - separatorPrice);
      const distanceToBottom = Math.abs(separatorPrice - dataExtents.min);

      // Use the larger distance to ensure all data is visible
      const maxDistance = Math.max(distanceToTop, distanceToBottom);

      // Add some padding (20% of the max distance)
      const padding = maxDistance * 0.2;
      const expandedDistance = maxDistance + padding;

      // Calculate new domain centered on separator with enough range to show all data
      const newMin = separatorPrice - expandedDistance;
      const newMax = separatorPrice + expandedDistance;
      const newDomain = [newMin, newMax];

      console.log("Calculating smart Y-axis recenter:", {
        separatorPrice,
        dataExtents,
        distanceToTop,
        distanceToBottom,
        maxDistance,
        padding,
        oldDomain: currentDomain,
        newDomain,
        chartId: moreProps.chartId,
      });

      // Dispatch a custom event that ChartCanvas can listen to
      const recenterEvent = new CustomEvent("baselineRecenter", {
        detail: {
          chartId: moreProps.chartId || 0,
          newDomain: newDomain,
          separatorPrice: separatorPrice,
          dataExtents: dataExtents,
        },
      });

      // Dispatch on window to ensure it can be caught
      window.dispatchEvent(recenterEvent);
    }
  }

  handleDragStart(moreProps) {
    const { separatorPrice } = this.state;
    const {
      chartConfig: { yScale },
    } = moreProps;
    const {
      mouseXY: [, mouseY],
    } = moreProps;

    this.dragStart = {
      startSeparatorPrice: separatorPrice,
      startY: mouseY,
      yScale,
    };

    this.setState({ isDragging: true });
  }

  handleDrag(moreProps) {
    const {
      mouseXY: [, mouseY],
    } = moreProps;
    const {
      chartConfig: { yScale },
    } = moreProps;

    if (!this.dragStart) return;

    const { startSeparatorPrice, startY } = this.dragStart;
    const deltaY = mouseY - startY;

    // Convert the Y pixel change to price change
    const newPrice = yScale.invert(yScale(startSeparatorPrice) + deltaY);

    // Use requestAnimationFrame for smooth updates instead of throttling
    if (this.dragAnimationFrame) {
      cancelAnimationFrame(this.dragAnimationFrame);
    }

    this.dragAnimationFrame = requestAnimationFrame(() => {
      this.setState({ dragSeparatorPrice: newPrice });
    });
  }

  handleDragComplete() {
    const { dragSeparatorPrice } = this.state;

    // Clean up animation frame
    if (this.dragAnimationFrame) {
      cancelAnimationFrame(this.dragAnimationFrame);
      this.dragAnimationFrame = null;
    }

    if (dragSeparatorPrice !== null) {
      this.setState({
        separatorPrice: dragSeparatorPrice,
        dragSeparatorPrice: null,
        isDragging: false, // Reset drag state
      });
    } else {
      this.setState({ isDragging: false });
    }

    this.dragStart = null;
  }

  handleHover(hovering) {
    this.setState({ hovering });
  }

  render() {
    const {
      yAccessor,
      greenFill,
      redFill,
      separatorStroke,
      lineStroke,
      enabled,
      showHandle,
      showLine,
      interpolation,
      className,
      opacity,
    } = this.props;

    const {
      hovering,
      dragSeparatorPrice,
      separatorPrice: stateSeparatorPrice,
    } = this.state;

    // Use drag price during drag, otherwise use state separator
    const separatorPrice = dragSeparatorPrice ?? stateSeparatorPrice;

    return (
      <g className={className}>
        {/* Hidden component to calculate separator from chart data */}
        <GenericChartComponent
          svgDraw={() => null}
          drawOn={["mousemove", "pan"]}
          canvasDraw={(ctx, moreProps) => {
            this.calculateSeparatorFromData(moreProps);
          }}
        />

        {/* Hidden component to handle Y-axis zoom recentering */}
        <GenericChartComponent
          svgDraw={() => null}
          drawOn={["yAxisZoom"]}
          canvasDraw={(ctx, moreProps) => {
            this.handleYAxisZoom(moreProps);
          }}
        />

        {/* Line series with color changes at separator */}
        {showLine && (
          <BaselineLineSeries
            yAccessor={yAccessor}
            separatorPrice={separatorPrice}
            greenStroke={{
              color: "#4CAF50",
              width: lineStroke?.width || 1,
              opacity: lineStroke?.opacity || 1,
            }}
            redStroke={{
              color: "#F44336",
              width: lineStroke?.width || 1,
              opacity: lineStroke?.opacity || 1,
            }}
            interpolation={interpolation}
            className={`${className}-line`}
          />
        )}

        {/* Above separator area (green) */}
        <BaselineAreaSeries
          yAccessor={yAccessor}
          separatorPrice={separatorPrice}
          isAbove={true}
          fill={greenFill}
          opacity={opacity}
          interpolation={interpolation}
          className={`${className}-above`}
        />

        {/* Below separator area (red) */}
        <BaselineAreaSeries
          yAccessor={yAccessor}
          separatorPrice={separatorPrice}
          isAbove={false}
          fill={redFill}
          opacity={opacity}
          interpolation={interpolation}
          className={`${className}-below`}
        />

        {/* Separator line */}
        <BaselineSeparator
          separatorPrice={separatorPrice}
          stroke={separatorStroke}
          hovering={hovering}
          className={`${className}-separator`}
        />

        {/* Draggable handle */}
        {enabled && showHandle && (
          <BaselineHandle
            separatorPrice={separatorPrice}
            enabled={enabled}
            hovering={hovering}
            onHover={() => this.handleHover(true)}
            onUnHover={() => this.handleHover(false)}
            onDragStart={this.handleDragStart}
            onDrag={this.handleDrag}
            onDragComplete={this.handleDragComplete}
            className={`${className}-handle`}
          />
        )}
      </g>
    );
  }
}

BaselineSeries.propTypes = {
  yAccessor: PropTypes.func.isRequired,
  greenFill: PropTypes.string,
  redFill: PropTypes.string,
  separatorStroke: PropTypes.shape({
    color: PropTypes.string,
    opacity: PropTypes.number,
    width: PropTypes.number,
    dash: PropTypes.string,
  }),
  lineStroke: PropTypes.shape({
    color: PropTypes.string,
    opacity: PropTypes.number,
    width: PropTypes.number,
    dash: PropTypes.string,
  }),
  enabled: PropTypes.bool,
  showHandle: PropTypes.bool,
  showLine: PropTypes.bool,
  interpolation: PropTypes.func,
  className: PropTypes.string,
  opacity: PropTypes.number,
};

BaselineSeries.defaultProps = {
  greenFill: "#4CAF50",
  redFill: "#F44336",
  separatorStroke: {
    color: "#607D8B",
    opacity: 0.8,
    width: 1,
    dash: "ShortDash",
  },
  lineStroke: {
    color: "#607D8B",
    opacity: 1,
    width: 2,
    dash: "Solid",
  },
  enabled: true,
  showHandle: true,
  showLine: true,
  interpolation: curveLinear,
  className: "react-stockcharts-baseline-series",
  opacity: 0.6, // Higher opacity since gradient handles transparency
};

export default BaselineSeries;
